import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';
import i18next from 'i18next';

import { format } from 'date-fns';
import { checkToken, checkAppVersion } from 'services/auth';
import { LOG_BAG } from 'utils/log';
import {
  addPeopleAmountToTableTitle,
  DECLINE_REASON_BY_CLIENT,
  DECLINE_REASON_NO_SHOW,
  isReservedAutomatically,
  isReservationsConfigFinished,
} from 'utils/reservation';
import { loadCssFile } from 'utils/general';
import { FREQUENCY } from 'config';
import ReservationsModal from 'app/components/reservations/ReservationsModal';
import ReservationsCalendar from 'app/components/reservations/ReservationsCalendar';
import {
  declineReservation,
  selectReservations,
  selectReservationsLoading,
  setReservations,
  setReservationsLoading,
  updateReservation,
} from 'store/reservations';
import PendingOrdersContainer from '../containers/PendingOrdersContainer';
import OrdersFetcher from '../containers/OrdersFetcher';
import {
  selectRestaurant,
  updateOrdersConfig,
  selectBookingConfig,
  selectRestaurantTables,
  selectOrdersConfig,
} from 'store/restaurant';
import { selectUserRole } from 'store/app';
import {
  selectPlaces,
  loadRestaurantPlaces,
  selectPlacesIsLoading,
} from 'store/restaurant-places';
import Restaurant, { hasReservationsPrint } from 'utils/restaurant';
import PreviewLink, { RestaurantPreviewType } from 'app/views/ViewPreviewLink';
import ReservationsDaysLimitFilter, {
  daysLimitFilterOptions,
} from 'app/components/reservations/ReservationsDaysLimitFilter';

import ReservationConfigNotFinishedAlert from 'app/components/alerts/ReservationConfigNotFinishedAlert';
import ReservationsFetcher from '../containers/ReservationsFetcher';
import type {
  ISavedFile,
  ISavedReservation,
  ITableWithPlace,
  ITableWithPlaceName,
  OrdersConfig,
  NormalizedRestaurant,
} from 'types';
import { IAppState } from 'store/main';
import CollapsibleCard from 'app/components/common/CollapsibleCard';
import AddReservationForm from './AddReservationFormikWrapper';
import {
  loadTableSuggestions,
  resetFindPlace,
  selectCalendarPreferences,
  selectProposedTables,
  selectReservationProposal,
  selectReservationFormValues,
  selectTableSuggestions,
  selectTableSuggestionsLoading,
  toggleTableSelection,
  updateCalendarPreferences,
} from 'store/reservation-calendar';
import RestaurantIsNotLiveAlert from 'app/components/alerts/RestaurantIsNotLiveAlert';
import OtoSpinner from 'app/components/common/OtoSpinner';
import ReservationsLegend from 'app/components/reservations/ReservationsLegend';
import ReservationReleaseNotes from 'app/components/reservations/ReservationReleaseNotes';
import ViewReservationsPlace from 'app/views/ViewReservationsPlace';
import ErrorBoundary from '../containers/ErrorBoundary';
import FileUpload from 'app/components/common/FileUpload';
import ReservationsViewOptions from 'app/components/reservations/ReservationsViewOptions';
import { withRouter, WithRouterProps } from 'app/containers/withRouter';
import OtoAlert from 'app/components/common/OtoAlert';
import GuestCard from 'app/components/reservations/GuestCard';
import {
  createCustomerNoteByPhone,
  getCustomerNotesForReservation,
  loadCustomerNotes,
  selectAddCustomerNoteLoading,
  selectCustomerNotes,
  selectCustomerNotesLoading,
} from 'store/customers';
import ReservationMarketingAgreements from 'app/components/reservations/ReservationMarketingAgreements';
import { ROLES } from 'enums';
import { downloadReservationsCSV } from 'utils/csv';

interface ReservationsCalendarPageState {
  daysLimitFilter: string;
  modalReservation: ISavedReservation | null;
  flexibleTables: boolean;
  tablesConfig: ITableWithPlaceName[];
}

type ReservationsListPageProps = PropsFromRedux & WithRouterProps;

const COMPONENT_NAME = 'ReservationsCalendarPage';
class ReservationsCalendarPage extends React.PureComponent<
  ReservationsListPageProps,
  ReservationsCalendarPageState
> {
  reloadInterval: number | null = null;
  _isMounted: boolean = false;

  constructor(props: ReservationsListPageProps) {
    super(props);
    this.checkUpToDateVersion();
    if (checkToken()) {
      const config = props.config || {};
      const flexibleTables = config.flexible_tables;
      this.state = {
        daysLimitFilter: daysLimitFilterOptions[0].value,
        modalReservation: null,
        flexibleTables,
        tablesConfig: [],
      };
      if (flexibleTables) {
        const tablesConfig = props.tablesConfig.map((tableConfig) => ({
          ...tableConfig,
          placeName: tableConfig.place!.name,
          nameWithPeopleAmount: `${tableConfig.name} (${tableConfig.min_people}-${tableConfig.max_people} os.)`,
        }));
        this.state = {
          ...this.state,
          tablesConfig,
        };
      }
    }
  }

  componentDidMount() {
    this._isMounted = true;
    const { loadCustomerNotes, loadRestaurantPlaces, restaurant, places } =
      this.props;
    if (!restaurant) {
      return;
    }
    if (hasReservationsPrint(restaurant)) {
      loadCssFile('reservations-rollpaper-print.css');
    }
    if (this.state.flexibleTables && !places?.length) {
      loadRestaurantPlaces(restaurant);
    }
    loadCustomerNotes(restaurant.id);
  }

  componentDidCatch(error, info) {
    LOG_BAG.logComponentDidCatch(COMPONENT_NAME, error, info);
    this.showGeneralToastError();
  }

  componentWillUnmount() {
    this.reloadInterval && clearInterval(this.reloadInterval);
    this._isMounted = false;
    this.props.resetFindPlace();
  }

  checkUpToDateVersion = () => {
    this.reloadInterval = setInterval(checkAppVersion, FREQUENCY.CHECK_VERSION);
    checkAppVersion();
  };

  showGeneralToastError = () => {
    toast.error(i18next.t('reservations-calendar-page.general-error'), {
      autoClose: false,
    });
  };

  handleReservationDelete = (reservation: ISavedReservation) => {
    this.props
      .declineReservation({
        reservation,
        successMessage: i18next.t('reservations-calendar-page.reservation-deleted'),
        errorMessage: i18next.t('reservations-calendar-page.reservation-delete-error'),
        declineCode: DECLINE_REASON_BY_CLIENT,
      })
      .then(() => this.closeModal());
  };

  handleReportNoShow = (reservation: ISavedReservation) => {
    this.props
      .declineReservation({
        reservation,
        successMessage: i18next.t('reservations-calendar-page.reservation-updated'),
        errorMessage: i18next.t('reservations-calendar-page.reservation-update-error'),
        declineCode: DECLINE_REASON_NO_SHOW,
      })
      .then(() => this.closeModal());
  };

  openModal = (reservation: ISavedReservation) =>
    this.setState({
      modalReservation: {
        ...reservation,
        ...(reservation.tables
          ? {
              tables: reservation.tables.map(
                (table) =>
                  addPeopleAmountToTableTitle(table, {
                    addPlace: true,
                    allTables: this.state.tablesConfig,
                  }) as ITableWithPlace // TODO fix types here
              ),
            }
          : {}),
      },
    });

  closeModal = () => this.setState({ modalReservation: null });

  navigateToAddReservation = ({
    date,
    table,
  }: {
    date: string;
    table?: { id: string };
  }) => {
    const tableUriPart = table && table.id ? `/${table.id}` : '';
    this.props.navigate(`/add-reservation/${date}${tableUriPart}`);
  };

  handleChangeDaysLimitFilter = (e) => {
    this.reloadInterval && clearInterval(this.reloadInterval);
    this.setState({ daysLimitFilter: e.target.value });
  };

  handleDownloadReservations = (dateFrom: Date, dateTo: Date) => {
    const reservations = (this.props.reservations || []).filter((r) => {
      const reservedOn = new Date(r.reserved_on);
      return reservedOn >= dateFrom && reservedOn < dateTo;
    });

    const formattedFrom = format(dateFrom, 'yyyy-MM-dd');
    const formattedTo = format(dateTo, 'yyyy-MM-dd');
    const datePart = formattedFrom === formattedTo
      ? formattedFrom
      : `${formattedFrom}-${formattedTo}`;
    downloadReservationsCSV(reservations, `reservations-${datePart}`);
  };

  handleTableSelectOnMap = (table: ITableWithPlace) => {
    this.props.toggleTableSelection(
      addPeopleAmountToTableTitle(table, {
        addPlace: true,
        allTables: this.state.tablesConfig,
      })
    );
  };

  handleCustomerNoteAdd = async ({
    note,
    modalReservation,
    restaurant,
  }: {
    note: string;
    modalReservation: ISavedReservation;
    restaurant: NormalizedRestaurant;
  }): Promise<boolean> => {
    try {
      const createdCustomerNote = await this.props.createCustomerNoteByPhone({
        customerPhone: modalReservation.phone_number,
        note,
        restaurantId: restaurant.id,
      });
      return !!createdCustomerNote;
    } catch (e) {
      return false;
    }
  };

  render() {
    const {
      calendarPreferences,
      isPlacesLoading,
      customerNotes,
      customerNotesLoading,
      customerNotesNewNoteLoading,
      config,
      ordersConfig,
      loadTableSuggestions,
      places,
      reservations,
      reservationProposal,
      reservationsLoading,
      tableSuggestionsLoading,
      tableSuggestions,
      updateCalendarPreferences,
    } = this.props;
    const restaurant = this.props.restaurant as NormalizedRestaurant;

    if (!isReservationsConfigFinished(config)) {
      return <ReservationConfigNotFinishedAlert />;
    }

    const { flexibleTables, modalReservation, tablesConfig, daysLimitFilter } =
      this.state;
    const reservationsWithoutCode = (reservations || []).filter(
      (res) => !res.code
    );
    if (reservationsWithoutCode.length) {
      LOG_BAG.logError(
        `ReservationsList found: ${reservationsWithoutCode.length} items without code`,
        reservationsWithoutCode
      );
    }

    return (
      <>
        <OrdersFetcher
          ordersConfig={ordersConfig}
          frequency={FREQUENCY.REFRESH_SECONDARY}
        />
        <ReservationsFetcher
          config={config}
          daysLimitFilter={this.state.daysLimitFilter}
          frequency={FREQUENCY.REFRESH}
          includeTablesAndTheirPlaces={this.state.flexibleTables}
          restaurant={restaurant}
        />
        <PendingOrdersContainer />
        <PreviewLink
          color="link"
          className="px-0"
          restaurant={restaurant}
          type={RestaurantPreviewType.Resevations}
        />
        {!restaurant.restaurant_opening_hours.length && (
          <OtoAlert color="danger">
            {i18next.t('reservations-calendar-page.missing-opening-hours')}
          </OtoAlert>
        )}
        <ReservationsLegend />
        <ReservationReleaseNotes restaurant={restaurant} />
        <CollapsibleCard
          className={'no-print find-place-card'}
          title={i18next.t('reservations-calendar-page.find-place-card-title')}
        >
          {(open) =>
            open ? (
              <>
                <AddReservationForm
                  allTables={tablesConfig}
                  config={config}
                  hasCalendar={true}
                  restaurant={restaurant}
                  // @TODO use proper navigation
                  onReservationCreate={() => this.props.navigate('/calendar')}
                  type="findPlace"
                />
                <hr className="my-2" />
                <ErrorBoundary componentName="ReservationsPlace">
                  <ViewReservationsPlace
                    onReservationClick={this.openModal}
                    onTableSelect={this.handleTableSelectOnMap}
                  />
                </ErrorBoundary>
              </>
            ) : null
          }
        </CollapsibleCard>
        <RestaurantIsNotLiveAlert type="reservations" restaurant={restaurant} />
        {(reservationsLoading || isPlacesLoading) && (
          <OtoSpinner className="mb-2" />
        )}
        <ReservationsViewOptions
          places={places}
          isPlacesLoading={isPlacesLoading}
          preferences={calendarPreferences}
          showPlaceSelect={flexibleTables}
          showHours={flexibleTables}
          updatePreferences={updateCalendarPreferences}
        >
          <ReservationsDaysLimitFilter
            onChange={this.handleChangeDaysLimitFilter}
            value={daysLimitFilter}
          />
        </ReservationsViewOptions>
        <ReservationsCalendar
          config={config}
          onOpenModalClick={this.openModal}
          onCalendarDateClick={this.navigateToAddReservation}
          onDownloadReservations={
            this.props.role !== ROLES.RESTAURANT_STAFF
              ? this.handleDownloadReservations
              : undefined
          }
          onProposedTablesSelect={this.props.selectProposedTables}
          places={places}
          preferences={this.props.calendarPreferences}
          restaurant={restaurant}
          reservationFormValues={this.props.reservationFormValues}
          reservations={reservations}
          reservationProposal={reservationProposal}
          tables={tablesConfig}
          tableSuggestions={this.props.tableSuggestions}
          updateReservation={this.props.updateReservation}
          updateCalendarPreferences={this.props.updateCalendarPreferences}
        />
        {!!modalReservation && (
          <ErrorBoundary componentName={'ReservationsModal'}>
            <ReservationsModal
              reservation={modalReservation}
              restaurant={restaurant}
              config={config}
              tables={flexibleTables ? tablesConfig : null}
              onSave={this.props.updateReservation}
              onClose={this.closeModal}
              onCancel={this.handleReservationDelete}
              onLogError={LOG_BAG.logError}
              loadTableSuggestions={loadTableSuggestions}
              renderFileUpload={this.renderFileUpload}
              reportNoShow={this.handleReportNoShow}
              suggestedTables={tableSuggestions}
              tableSuggestionsLoading={tableSuggestionsLoading}
            >
              <GuestCard
                customerNotes={getCustomerNotesForReservation(
                  customerNotes,
                  modalReservation
                )}
                className={'mt-3'}
                isLoadingCustomerNotes={customerNotesLoading}
                isNoteBeingCreated={customerNotesNewNoteLoading}
                onNoteAdd={(note: string) =>
                  this.handleCustomerNoteAdd({
                    note,
                    modalReservation,
                    restaurant,
                  })
                }
              />
              {Restaurant.wantsToCollectMarketingAgreements(restaurant) && (
                <ReservationMarketingAgreements
                  className={'mt-2'}
                  isAddedByRestaurant={!isReservedAutomatically(modalReservation)}
                  reservation={modalReservation}
                />
              )}
            </ReservationsModal>
          </ErrorBoundary>
        )}
      </>
    );
  }

  renderFileUpload = ({
    onSuccess,
  }: {
    onSuccess: (file: ISavedFile) => void;
  }) => {
    return <FileUpload onSuccess={onSuccess} />;
  };
}
const mapStateToProps = (state: IAppState) => ({
  calendarPreferences: selectCalendarPreferences(state),
  config: selectBookingConfig(state),
  customerNotes: selectCustomerNotes(state),
  customerNotesLoading: selectCustomerNotesLoading(state),
  customerNotesNewNoteLoading: selectAddCustomerNoteLoading(state),

  ordersConfig: selectOrdersConfig(state) as OrdersConfig,

  reservationFormValues: selectReservationFormValues(state),
  reservations: selectReservations(state),
  reservationsLoading: selectReservationsLoading(state),

  restaurant: selectRestaurant(state),
  role: selectUserRole(state),

  places: selectPlaces(state),
  isPlacesLoading: selectPlacesIsLoading(state),

  reservationProposal: selectReservationProposal(state),
  tableSuggestions: selectTableSuggestions(state),
  tableSuggestionsLoading: selectTableSuggestionsLoading(state),

  tablesConfig: selectRestaurantTables(state),
});

const mapDispatchToProps = {
  createCustomerNoteByPhone,
  declineReservation,
  loadCustomerNotes,
  loadTableSuggestions,
  loadRestaurantPlaces,
  resetFindPlace,
  selectProposedTables,
  setReservations,
  setReservationsLoading,
  toggleTableSelection,
  updateReservation,
  updateOrdersConfig,
  updateCalendarPreferences,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withRouter(ReservationsCalendarPage));
